import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * ORIVON product page, below the fold (sections/orivon-pdp-packs.liquid, sections/orivon-pdp-bundle.liquid).
 * Everything is server-rendered; this module only keeps two things in step with the shopper:
 *
 * 1. <orivon-pdp-packs-component data-product-id>: the ring (.is-current) on the pack card of the variant chosen in
 *    the buy box. Cards carry data-o-variant-id.
 * 2. <orivon-pdp-bundle-component data-product-id data-money-format data-currency data-label data-empty-label
 *    data-selection-template data-announce-template>:
 *    "Build the bundle". Ticking items recounts the total and the MRP saving, rewrites the data-lines of the nested
 *    <orivon-add-lines-component> (assets/orivon-cart.js, one /cart/add.js request on Horizon's CartLinesUpdateEvent
 *    contract) and its label ("Add 3 to cart · ₹777"). Line 1 is this product: it follows the buy box's variant
 *    by showing the matching server-rendered alternates ([data-o-fbt-alt="{variant id}"]).
 *    The "2 of 3" count and the announcement use [count] / [total] templates rendered from locales/en.default.json
 *    (orivon.pdp.bundle_selection / bundle_announce); the English strings here are fallbacks only.
 *
 * Both listen to Horizon's product-select event (StandardEvents.productSelect, dispatched by the variant picker and
 * bubbling to document) and use the variant JSON its promise resolves with. Events from product cards, quick add
 * dialogs or other products are ignored. Editor-safe: listeners are tied to the element's lifetime.
 */

/**
 * Calls `callback` with the variant id whenever the main product's variant picker selects a variant.
 * @param {string} productId - The page product's id
 * @param {AbortSignal} signal - Removes the listener
 * @param {(variantId: string) => void} callback
 */
function onMainVariantSelect(productId, signal, callback) {
  document.addEventListener(
    StandardEvents.productSelect,
    (event) => {
      const source = event.target;
      if (source instanceof Element && source.closest('product-card, dialog, quick-add-component')) return;

      const promise = /** @type {any} */ (event).promise;
      if (!promise || typeof promise.then !== 'function') return;

      promise
        .then((/** @type {any} */ result) => {
          const detail = result?.detail;
          const resource = detail?.resource;
          if (!resource || resource.id == null || detail.newProduct) return;
          if (detail.productId && String(detail.productId) !== String(productId)) return;
          callback(String(resource.id));
        })
        .catch(() => {});
    },
    { signal }
  );
}

/** Pack comparison: rings the card of the variant chosen in the buy box. */
class OrivonPdpPacksComponent extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const productId = this.dataset.productId;
    if (productId) onMainVariantSelect(productId, this.#controller.signal, (id) => this.select(id));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** @param {string} variantId */
  select(variantId) {
    for (const card of this.querySelectorAll('[data-o-variant-id]')) {
      card.classList.toggle('is-current', card.getAttribute('data-o-variant-id') === variantId);
    }
  }
}

/** "Build the bundle": checkboxes, live total, MRP saving and the add-selected button. */
class OrivonPdpBundleComponent extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.matches('[data-o-fbt-check]')) this.update(true);
      },
      { signal }
    );

    const productId = this.dataset.productId;
    if (productId) onMainVariantSelect(productId, signal, (id) => this.selectVariant(id));

    this.update(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** @returns {HTMLElement[]} */
  get items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-o-fbt-item]')]);
  }

  /**
   * Money like Liquid's money_without_trailing_zeros: ₹249 · ₹178.50 · ₹1,399.
   * @param {number} cents
   */
  money(cents) {
    const format = this.dataset.moneyFormat || '₹{{amount}}';
    const currency = this.dataset.currency || 'INR';
    const pattern = cents % 100 === 0 ? format.replace(/{{\s*amount\s*}}/g, '{{amount_no_decimals}}') : format;
    try {
      return formatMoney(cents, pattern, currency);
    } catch (error) {
      return `₹${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
    }
  }

  /** @param {HTMLElement} item */
  #checkbox(item) {
    const input = item.querySelector('[data-o-fbt-check]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Recounts the selection and rewrites the summary and the add button.
   * @param {boolean} announce - Read the new total out through the live region
   */
  update(announce) {
    const items = this.items;
    const on = items.filter((item) => {
      const input = this.#checkbox(item);
      return Boolean(input && input.checked && !input.disabled);
    });

    let total = 0;
    let save = 0;
    for (const item of on) {
      const price = Number(item.dataset.oPrice) || 0;
      const compare = Number(item.dataset.oCompare) || 0;
      total += price;
      if (compare > price) save += compare - price;
    }

    const set = (/** @type {string} */ selector, /** @type {string} */ text) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = text;
    };
    set(
      '[data-o-fbt-count]',
      (this.dataset.selectionTemplate || '[count] of [total]')
        .replace(/\[count\]/g, String(on.length))
        .replace(/\[total\]/g, String(items.length))
    );
    set('[data-o-fbt-total]', this.money(total));
    set('[data-o-fbt-save]', this.money(save));
    const saveWrap = this.querySelector('[data-o-fbt-save-wrap]');
    if (saveWrap instanceof HTMLElement) saveWrap.hidden = save <= 0;

    const adder = this.querySelector('orivon-add-lines-component');
    if (adder instanceof HTMLElement) {
      adder.dataset.lines = on.map((item) => `${item.dataset.oVariantId}:1`).join(',');
      const button = adder.querySelector('button');
      if (button) {
        if (on.length) {
          button.removeAttribute('aria-disabled');
          button.disabled = false;
        } else {
          button.setAttribute('aria-disabled', 'true');
        }
      }
      const label = adder.querySelector('.o-buy-button__label');
      if (label) {
        label.textContent = on.length
          ? (this.dataset.label || 'Add [count] to cart · [total]')
              .replace(/\[count\]/g, String(on.length))
              .replace(/\[total\]/g, this.money(total))
          : this.dataset.emptyLabel || 'Tick at least one item';
      }
    }

    if (announce) {
      set(
        '[data-o-fbt-live]',
        (this.dataset.announceTemplate || '[count] selected. Bundle total [total].')
          .replace(/\[count\]/g, String(on.length))
          .replace(/\[total\]/g, () => this.money(total))
      );
    }
  }

  /**
   * Line 1 follows the buy box: show the chosen variant's image, size and price.
   * @param {string} variantId
   */
  selectVariant(variantId) {
    const item = this.querySelector('[data-o-fbt-item][data-o-self]');
    if (!(item instanceof HTMLElement)) return;
    const alternates = [...item.querySelectorAll('[data-o-fbt-alt]')];
    const data = item.querySelector(`[data-o-fbt-alt="${variantId}"][data-o-price]`);
    if (!(data instanceof HTMLElement)) return;

    for (const element of alternates) {
      if (element instanceof HTMLElement) element.hidden = element.getAttribute('data-o-fbt-alt') !== variantId;
    }

    item.dataset.oVariantId = variantId;
    item.dataset.oPrice = data.dataset.oPrice || '0';
    item.dataset.oCompare = data.dataset.oCompare || '0';

    const input = this.#checkbox(item);
    if (input) {
      const available = data.dataset.oAvailable !== 'false';
      if (!available) input.checked = false;
      else if (input.disabled) input.checked = true;
      input.disabled = !available;
    }
    this.update(false);
  }
}

if (!customElements.get('orivon-pdp-packs-component')) {
  customElements.define('orivon-pdp-packs-component', OrivonPdpPacksComponent);
}
if (!customElements.get('orivon-pdp-bundle-component')) {
  customElements.define('orivon-pdp-bundle-component', OrivonPdpBundleComponent);
}
